import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * EXCEL BENZERİ TABLO DÜZENİ — kolon başlığından süzme ve ÇOK KOLONLU sıralama.
 *
 * ⚠️ ÇOK KOLONLU SIRALAMA BU MODÜLÜN VARLIK SEBEBİDİR, süsü değil.
 *
 * Kullanıcının bugün Excel'de yaptığı analiz şu: *"Değirmen 5'i seç, o
 * ünitedeki iskeleleri en eski kontrolden en yeniye sırala."* Tek kolonluk
 * sıralama bunu karşılamaz — ünitelere göre sıralayınca ünite içindeki
 * sıra rastgele kalır, kontrol tarihine göre sıralayınca üniteler dağılır.
 * "Hatırlatma probleminin" çözümü ikisinin AYNI ANDA olmasıdır.
 *
 * ⚠️ HER ŞEY İSTEMCİDE. Liste zaten bütünüyle indirilmiş durumda; süzmeyi
 *    sunucuya taşımak, sinyalsiz fabrikada (T1) tabloyu süzülemez ve
 *    sıralanamaz yapardı. Kullanıcı çevrimdışıyken de analizini yapabilmeli.
 */

export type Yon = 'asc' | 'desc';

/**
 * Kolon tipi — SIRALAMA VE SÜZGEÇ DAVRANIŞINI belirler.
 *
 *   metin     serbest metin      → içerir araması
 *   kategori  sınırlı değer kümesi → onay kutulu liste (Excel'in AutoFilter'ı)
 *   sayi      ölçülebilir         → en az / en çok
 *   gun       "N gün önce"        → sayı gibi ama YÖN TERSİNE OKUNUR
 *
 * `gun` neden ayrı: kullanıcı "en eski" derken gün sayısının BÜYÜK olmasını
 * kasteder. Ekranda "3 gün önce" yazan satır, "40 gün önce" yazandan daha
 * yenidir. Etiketleri "artan/azalan" diye yazsaydık her seferinde kafadan
 * çevirmek gerekirdi.
 */
export type KolonTipi = 'metin' | 'kategori' | 'sayi' | 'gun';

export interface Kolon<T> {
  anahtar: string;
  baslik: string;
  tip: KolonTipi;
  /** Sıralama ve süzme DEĞERİ — ekranda görünen değil, karşılaştırılan. */
  deger: (satir: T) => string | number | null;
  /** Sayı kolonlarında birim ("m³", "gün") — süzgeç kutusunda gösterilir. */
  birim?: string;
  sagaYasli?: boolean;
  /** Süzgeç menüsü açılmasın (ör. serbest metin sütunu çok dağınıksa). */
  suzgecsiz?: boolean;
}

interface KategoriSuzgec {
  tip: 'kategori';
  /** Boş küme = süzgeç yok (hepsi). Excel'de de "tümü seçili" varsayılandır. */
  secili: string[];
}
interface AralikSuzgec {
  tip: 'aralik';
  min: number | null;
  max: number | null;
  /** Değeri olmayan satırlar (ör. "hiç kontrol edilmedi") dahil edilsin mi? */
  bosDahil: boolean;
}
interface MetinSuzgec {
  tip: 'metin';
  q: string;
}
export type Suzgec = KategoriSuzgec | AralikSuzgec | MetinSuzgec;

export interface TabloDuzeni {
  sira: { anahtar: string; yon: Yon }[];
  suzgecler: Record<string, Suzgec>;
}

/** Türkçe duyarlı, sayı farkeden metin karşılaştırması. */
const metinKiyas = (a: string, b: string) =>
  a.localeCompare(b, 'tr', { numeric: true, sensitivity: 'base' });

/**
 * Sıralama karşılaştırıcısı — ÇOK ANAHTARLI.
 *
 * ⚠️ BOŞ DEĞERLER `gun` KOLONUNDA EN ESKİ SAYILIR, en sona atılmaz.
 *    "Hiç kontrol edilmedi" bir eksik veri değil, EN KÖTÜ DURUMDUR; kuralın
 *    kendisi de böyle diyor (`kontrolVadesiGecti: kontrolGun == null || >= 7`).
 *    Sona atılsaydı, ekranın tek amacı olan "en riskliyi en üstte göster"
 *    tam da en riskli satırlarda tersine dönerdi.
 */
function kiyasla<T>(kolonlar: Kolon<T>[], sira: TabloDuzeni['sira']) {
  const harita = new Map(kolonlar.map((k) => [k.anahtar, k]));
  return (a: T, b: T): number => {
    for (const s of sira) {
      const k = harita.get(s.anahtar);
      if (!k) continue;
      const av = k.deger(a);
      const bv = k.deger(b);

      let d: number;
      if (av == null && bv == null) d = 0;
      else if (av == null) d = k.tip === 'gun' ? 1 : -1;
      else if (bv == null) d = k.tip === 'gun' ? -1 : 1;
      else if (typeof av === 'number' && typeof bv === 'number') d = av - bv;
      else d = metinKiyas(String(av), String(bv));

      if (d !== 0) return s.yon === 'asc' ? d : -d;
    }
    return 0;
  };
}

function suzgectenGecer<T>(satir: T, kolonlar: Kolon<T>[], suzgecler: Record<string, Suzgec>) {
  for (const [anahtar, f] of Object.entries(suzgecler)) {
    const k = kolonlar.find((c) => c.anahtar === anahtar);
    if (!k) continue;
    const v = k.deger(satir);

    if (f.tip === 'kategori') {
      if (f.secili.length === 0) continue; // boş = süzme yok
      if (!f.secili.includes(String(v ?? ''))) return false;
    } else if (f.tip === 'metin') {
      if (!f.q.trim()) continue;
      const hedef = String(v ?? '').toLocaleLowerCase('tr');
      if (!hedef.includes(f.q.trim().toLocaleLowerCase('tr'))) return false;
    } else {
      if (v == null) {
        // Boş değerli satır: yalnız kullanıcı açıkça istediyse kalır.
        if (f.min == null && f.max == null) continue;
        if (!f.bosDahil) return false;
        continue;
      }
      const n = Number(v);
      if (f.min != null && n < f.min) return false;
      if (f.max != null && n > f.max) return false;
    }
  }
  return true;
}

/**
 * SAF MANTIK — süzme + sıralama tek yerde, React'ten bağımsız.
 *
 * Dışa açık olması test içindir: çok kolonlu sıralama bu ekranın gereksinimi
 * ve "hatırlatma probleminin" çözümü; bir bileşenin içine gömülü kalıp
 * doğrulanamaz olmamalı.
 */
export function siralayipSuz<T>(
  veri: readonly T[],
  kolonlar: Kolon<T>[],
  duzen: TabloDuzeni,
): T[] {
  const suzulmus = veri.filter((r) => suzgectenGecer(r, kolonlar, duzen.suzgecler));
  return [...suzulmus].sort(kiyasla(kolonlar, duzen.sira));
}

export function useTabloDuzeni<T>(
  veri: readonly T[] | null,
  kolonlar: Kolon<T>[],
  varsayilanSira: TabloDuzeni['sira'],
) {
  const [duzen, setDuzen] = useState<TabloDuzeni>({ sira: varsayilanSira, suzgecler: {} });

  const sonuc = useMemo(() => {
    if (!veri) return [];
    return siralayipSuz(veri, kolonlar, duzen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veri, duzen]);

  /**
   * SIRALAMA TIKLAMASI.
   *
   * `ekle=false` (düz tık)  → bu kolon TEK sıralama anahtarı olur
   * `ekle=true`  (Shift+tık) → mevcut sıralamanın SONUNA eklenir
   *
   * ⚠️ Düz tıkın mevcut sıralamayı SİLMESİ kasıtlı. Alternatifi — her tıkın
   *    eklemesi — kullanıcının farkında olmadan dört anahtarlı bir sıralama
   *    biriktirmesi ve tablonun neden o sırada olduğunu anlayamaması demekti.
   *    Ekleme AÇIK bir eylem: ya Shift ya da menüdeki "sıralamaya ekle".
   */
  const sirala = (anahtar: string, ekle = false) => {
    setDuzen((d) => {
      const mevcut = d.sira.find((s) => s.anahtar === anahtar);
      const yeniYon: Yon = mevcut?.yon === 'asc' ? 'desc' : 'asc';
      if (!ekle) return { ...d, sira: [{ anahtar, yon: yeniYon }] };
      if (mevcut) {
        return {
          ...d,
          sira: d.sira.map((s) => (s.anahtar === anahtar ? { ...s, yon: yeniYon } : s)),
        };
      }
      return { ...d, sira: [...d.sira, { anahtar, yon: 'asc' }] };
    });
  };

  const siradanCikar = (anahtar: string) =>
    setDuzen((d) => {
      const kalan = d.sira.filter((s) => s.anahtar !== anahtar);
      // Sıralamasız tablo satırları rastgele gösterirdi — varsayılana dön.
      return { ...d, sira: kalan.length ? kalan : varsayilanSira };
    });

  const suzgecYaz = (anahtar: string, f: Suzgec | null) =>
    setDuzen((d) => {
      const y = { ...d.suzgecler };
      if (f) y[anahtar] = f;
      else delete y[anahtar];
      return { ...d, suzgecler: y };
    });

  const temizle = () => setDuzen({ sira: varsayilanSira, suzgecler: {} });

  /**
   * DÜZENİ TOPTAN YAZ — hazır görünümler için.
   *
   * Dashboard kartları böyle çalışıyor: kart bir süzgeç + sıralama bileşimini
   * TEK adımda kuruyor. Ayrı bir "görünüm" durumu açmak yerine mevcut düzeni
   * yazmasının sebebi, sonucun özet şeridinde görünmesi ve normal yoldan
   * temizlenebilmesi — kullanıcı için iki ayrı süzme kavramı olmuyor.
   */
  const duzenYaz = (y: TabloDuzeni) => setDuzen(y);

  return {
    duzen,
    sonuc,
    sirala,
    siradanCikar,
    suzgecYaz,
    duzenYaz,
    temizle,
    suzgecSayisi: Object.keys(duzen.suzgecler).length,
  };
}

/* ================================================================== */
/*  BAŞLIK HÜCRESİ                                                     */
/* ================================================================== */

function OkIkon({ yon }: { yon: Yon }) {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
      <path
        d={yon === 'asc' ? 'M5 2 L9 8 L1 8 Z' : 'M5 8 L1 2 L9 2 Z'}
        fill="currentColor"
      />
    </svg>
  );
}

function HuniIkon({ dolu }: { dolu: boolean }) {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
      <path
        d="M1 2 h10 l-4 4.5 v4 l-2 -1.2 v-3.3 z"
        fill={dolu ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Sıralama yön etiketleri — KOLON TİPİNE göre, "artan/azalan" değil. */
function yonEtiketi(tip: KolonTipi, yon: Yon): string {
  if (tip === 'gun') return yon === 'asc' ? 'En yeni önce' : 'En eski önce';
  if (tip === 'sayi') return yon === 'asc' ? 'Küçükten büyüğe' : 'Büyükten küçüğe';
  return yon === 'asc' ? 'A → Z' : 'Z → A';
}

export function KolonBasi<T>({
  kolon,
  veri,
  duzen,
  sirala,
  siradanCikar,
  suzgecYaz,
}: {
  kolon: Kolon<T>;
  veri: readonly T[];
  duzen: TabloDuzeni;
  sirala: (anahtar: string, ekle?: boolean) => void;
  siradanCikar: (anahtar: string) => void;
  suzgecYaz: (anahtar: string, f: Suzgec | null) => void;
}) {
  const [acik, setAcik] = useState(false);
  const ref = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    if (!acik) return;
    const d = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAcik(false);
    };
    document.addEventListener('mousedown', d);
    return () => document.removeEventListener('mousedown', d);
  }, [acik]);

  const siraIndeks = duzen.sira.findIndex((s) => s.anahtar === kolon.anahtar);
  const sirali = siraIndeks >= 0 ? duzen.sira[siraIndeks] : null;
  const suzgec = duzen.suzgecler[kolon.anahtar];
  const suzgecliMi =
    !!suzgec &&
    ((suzgec.tip === 'kategori' && suzgec.secili.length > 0) ||
      (suzgec.tip === 'metin' && suzgec.q.trim() !== '') ||
      (suzgec.tip === 'aralik' && (suzgec.min != null || suzgec.max != null)));

  /** Kategori süzgeci için tablodaki GERÇEK değerler — sabit liste değil. */
  const degerler = useMemo(() => {
    if (kolon.tip !== 'kategori') return [];
    const s = new Set<string>();
    for (const r of veri) s.add(String(kolon.deger(r) ?? ''));
    return [...s].filter(Boolean).sort(metinKiyas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veri, kolon.anahtar]);

  return (
    <th
      ref={ref}
      className={`relative px-3 py-2 font-medium select-none ${kolon.sagaYasli ? 'text-right' : 'text-left'}`}
    >
      <span className={`flex items-center gap-1 ${kolon.sagaYasli ? 'justify-end' : ''}`}>
        <button
          onClick={(e) => sirala(kolon.anahtar, e.shiftKey)}
          title={`Sırala — birden çok kolona göre sıralamak için Shift ile tıklayın`}
          className="inline-flex items-center gap-1 rounded px-0.5 hover:text-navy"
        >
          {kolon.baslik}
          {sirali && (
            <span className="inline-flex items-center gap-0.5 text-navy">
              <OkIkon yon={sirali.yon} />
              {/* Sıra numarası YALNIZ çok anahtarlıyken — tek anahtarda gürültü. */}
              {duzen.sira.length > 1 && (
                <span className="num rounded-full bg-navy/10 px-1 text-[10px] leading-4 font-semibold">
                  {siraIndeks + 1}
                </span>
              )}
            </span>
          )}
        </button>

        {!kolon.suzgecsiz && (
          <button
            onClick={() => setAcik((v) => !v)}
            aria-label={`${kolon.baslik} sütununu süz`}
            className={`rounded p-0.5 ${suzgecliMi ? 'text-navy' : 'text-wait/60 hover:text-navy'}`}
          >
            <HuniIkon dolu={suzgecliMi} />
          </button>
        )}
      </span>

      {acik && (
        <div className="absolute top-full left-0 z-40 mt-1 w-60 rounded-(--radius-mod) border border-line bg-surface p-2 text-left font-normal shadow-lg">
          <div className="flex flex-col gap-0.5 border-b border-line/70 pb-2">
            {(['asc', 'desc'] as Yon[]).map((y) => (
              <button
                key={y}
                onClick={() => {
                  if (sirali?.yon !== y) sirala(kolon.anahtar, false);
                  else sirala(kolon.anahtar, false);
                  setAcik(false);
                }}
                className={`rounded px-2 py-1.5 text-left text-xs hover:bg-ground ${
                  sirali?.yon === y ? 'font-semibold text-navy' : 'text-ink-2'
                }`}
              >
                {yonEtiketi(kolon.tip, y)}
              </button>
            ))}
            {/*
              ÇOK KOLONLU SIRALAMANIN GÖRÜNEN KAPISI.
              Shift+tık bir kısayol; keşfedilebilir olması için burada da var.
            */}
            {!sirali && duzen.sira.length > 0 && (
              <button
                onClick={() => {
                  sirala(kolon.anahtar, true);
                  setAcik(false);
                }}
                className="rounded px-2 py-1.5 text-left text-xs text-ink-2 hover:bg-ground"
              >
                + Sıralamaya ekle{' '}
                <span className="text-wait">({duzen.sira.length + 1}. anahtar)</span>
              </button>
            )}
            {sirali && duzen.sira.length > 1 && (
              <button
                onClick={() => {
                  siradanCikar(kolon.anahtar);
                  setAcik(false);
                }}
                className="rounded px-2 py-1.5 text-left text-xs text-crit hover:bg-crit/5"
              >
                Sıralamadan çıkar
              </button>
            )}
          </div>

          <div className="pt-2">
            {kolon.tip === 'kategori' && (
              <KategoriKutusu
                degerler={degerler}
                secili={suzgec?.tip === 'kategori' ? suzgec.secili : []}
                onDegis={(s) =>
                  suzgecYaz(kolon.anahtar, s.length ? { tip: 'kategori', secili: s } : null)
                }
              />
            )}
            {kolon.tip === 'metin' && (
              <input
                autoFocus
                value={suzgec?.tip === 'metin' ? suzgec.q : ''}
                onChange={(e) =>
                  suzgecYaz(kolon.anahtar, e.target.value ? { tip: 'metin', q: e.target.value } : null)
                }
                placeholder="İçerir…"
                className="w-full rounded-(--radius-mod) border border-line-2 px-2 py-1.5 text-xs outline-none focus:border-navy"
              />
            )}
            {(kolon.tip === 'sayi' || kolon.tip === 'gun') && (
              <AralikKutusu
                birim={kolon.birim}
                bosEtiket={kolon.tip === 'gun' ? 'Hiç yapılmamışları da göster' : 'Boşları da göster'}
                deger={suzgec?.tip === 'aralik' ? suzgec : { tip: 'aralik', min: null, max: null, bosDahil: true }}
                onDegis={(f) =>
                  suzgecYaz(kolon.anahtar, f.min == null && f.max == null ? null : f)
                }
              />
            )}
          </div>
        </div>
      )}
    </th>
  );
}

function KategoriKutusu({
  degerler,
  secili,
  onDegis,
}: {
  degerler: string[];
  secili: string[];
  onDegis: (s: string[]) => void;
}) {
  const [ara, setAra] = useState('');
  const gorunen = degerler.filter((d) =>
    d.toLocaleLowerCase('tr').includes(ara.toLocaleLowerCase('tr')),
  );
  return (
    <div>
      {degerler.length > 8 && (
        <input
          value={ara}
          onChange={(e) => setAra(e.target.value)}
          placeholder="Ara…"
          className="mb-1.5 w-full rounded-(--radius-mod) border border-line-2 px-2 py-1 text-xs outline-none focus:border-navy"
        />
      )}
      <div className="mb-1.5 flex gap-2 text-[11px]">
        <button onClick={() => onDegis(gorunen)} className="text-navy hover:underline">
          Tümünü seç
        </button>
        <button onClick={() => onDegis([])} className="text-wait hover:underline">
          Temizle
        </button>
      </div>
      <div className="max-h-52 overflow-y-auto">
        {gorunen.map((d) => (
          <label key={d} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-ground">
            <input
              type="checkbox"
              checked={secili.includes(d)}
              onChange={(e) =>
                onDegis(e.target.checked ? [...secili, d] : secili.filter((x) => x !== d))
              }
            />
            <span className="truncate">{d}</span>
          </label>
        ))}
        {gorunen.length === 0 && <p className="px-1 py-2 text-xs text-wait">Değer yok.</p>}
      </div>
    </div>
  );
}

function AralikKutusu({
  deger,
  birim,
  bosEtiket,
  onDegis,
}: {
  deger: AralikSuzgec;
  birim?: string;
  bosEtiket: string;
  onDegis: (f: AralikSuzgec) => void;
}) {
  const sayi = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')));
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          inputMode="decimal"
          value={deger.min ?? ''}
          onChange={(e) => onDegis({ ...deger, min: sayi(e.target.value) })}
          placeholder="en az"
          className="num w-full rounded-(--radius-mod) border border-line-2 px-2 py-1 text-xs outline-none focus:border-navy"
        />
        <span className="text-xs text-wait">–</span>
        <input
          type="number"
          inputMode="decimal"
          value={deger.max ?? ''}
          onChange={(e) => onDegis({ ...deger, max: sayi(e.target.value) })}
          placeholder="en çok"
          className="num w-full rounded-(--radius-mod) border border-line-2 px-2 py-1 text-xs outline-none focus:border-navy"
        />
        {birim && <span className="shrink-0 text-xs text-wait">{birim}</span>}
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-[11px] text-ink-2">
        <input
          type="checkbox"
          checked={deger.bosDahil}
          onChange={(e) => onDegis({ ...deger, bosDahil: e.target.checked })}
        />
        {bosEtiket}
      </label>
    </div>
  );
}

/* ================================================================== */
/*  ÖZET ŞERİDİ — "tablo neden bu sırada" sorusunun cevabı             */
/* ================================================================== */

export function DuzenOzeti<T>({
  kolonlar,
  duzen,
  toplam,
  gorunen,
  onTemizle,
  onSiradanCikar,
  onSuzgecKaldir,
}: {
  kolonlar: Kolon<T>[];
  duzen: TabloDuzeni;
  toplam: number;
  gorunen: number;
  onTemizle: () => void;
  onSiradanCikar: (anahtar: string) => void;
  onSuzgecKaldir: (anahtar: string) => void;
}): ReactNode {
  const ad = (a: string) => kolonlar.find((k) => k.anahtar === a)?.baslik ?? a;
  const suzgecAnahtarlari = Object.keys(duzen.suzgecler);
  const cokluSira = duzen.sira.length > 1;

  if (!cokluSira && suzgecAnahtarlari.length === 0) return null;

  return (
    <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs">
      {cokluSira && (
        <>
          <span className="text-wait">Sıralama:</span>
          {duzen.sira.map((s, i) => (
            <button
              key={s.anahtar}
              onClick={() => onSiradanCikar(s.anahtar)}
              title="Sıralamadan çıkar"
              className="inline-flex items-center gap-1 rounded-full border border-navy/25 bg-navy/5 px-2 py-0.5 text-navy hover:bg-navy/10"
            >
              <span className="num opacity-60">{i + 1}.</span>
              {ad(s.anahtar)}
              <OkIkon yon={s.yon} />
              <span className="opacity-50">×</span>
            </button>
          ))}
        </>
      )}
      {suzgecAnahtarlari.length > 0 && (
        <>
          <span className="ml-1 text-wait">Süzgeç:</span>
          {suzgecAnahtarlari.map((a) => (
            <button
              key={a}
              onClick={() => onSuzgecKaldir(a)}
              title="Süzgeci kaldır"
              className="inline-flex items-center gap-1 rounded-full border border-info/30 bg-info/5 px-2 py-0.5 text-info hover:bg-info/10"
            >
              {ad(a)}
              <span className="opacity-50">×</span>
            </button>
          ))}
          <span className="num text-wait">
            {gorunen}/{toplam}
          </span>
        </>
      )}
      <button onClick={onTemizle} className="ml-1 text-wait underline hover:text-navy">
        Temizle
      </button>
    </div>
  );
}
